import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KpiCard } from "@/components/KpiCard";
import { formatBRL, formatPct, formatNum } from "@/lib/format";
import { Loader2, ArrowUpRight, Info, Rocket } from "lucide-react";
import { Badge } from "@/components/ui/badge";

function RankingCard({ title, items, render, testid, empty }) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm" data-testid={testid}>
      <div className="px-5 py-4 border-b border-border">
        <h3 className="font-display font-semibold">{title}</h3>
      </div>
      <div className="divide-y divide-border">
        {items.length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground">{empty}</p>}
        {items.map((it, i) => (
          <div key={it.id} className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-xs font-num text-muted-foreground w-4">{i + 1}</span>
              <div className="min-w-0">
                <p className="font-num text-sm font-semibold truncate">{it.code}</p>
                <p className="text-xs text-muted-foreground truncate">{it.supplier_name || "Sem fornecedor"}</p>
              </div>
            </div>
            <div className="text-right">{render(it)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onb, setOnb] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard");
      setData(res.data);
    } catch (e) {
      apiError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.id]);
  useEffect(() => { api.get("/onboarding/status").then((r) => setOnb(r.data)).catch(() => {}); }, [currentOrg?.id]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  if (!data) return null;

  const k = data.kpis;
  const marginTone = k.margin_pct == null ? "default" : k.margin_pct >= 0 ? "success" : "danger";

  return (
    <div>
      {onb && !onb.completed && (
        <div className="mb-6 rounded-xl border border-accent/40 bg-accent/5 p-4 flex items-center justify-between gap-4" data-testid="onboarding-banner">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-accent/15 flex items-center justify-center"><Rocket className="h-5 w-5 text-accent" /></div>
            <div>
              <p className="font-semibold">Configure sua operação</p>
              <p className="text-sm text-muted-foreground">Complete os primeiros passos para aproveitar o TRIA ao máximo.</p>
            </div>
          </div>
          <button onClick={() => navigate("/onboarding")} className="inline-flex items-center gap-1 text-sm text-accent font-semibold hover:underline whitespace-nowrap" data-testid="onboarding-banner-cta">
            Continuar <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Visão Gerencial Consolidada</h1>
        <p className="text-muted-foreground mt-1">{data.totals.lots} lote(s) · {formatNum(data.totals.qty_sold)} vendidas de {formatNum(data.totals.qty_received)} recebidas</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        <KpiCard testid="dashboard-kpi-rpa" label="Receita Projetada Atualizada (RPA)" value={formatBRL(k.rpa)} sub="RR + RPR" tone="accent" />
        <KpiCard testid="dashboard-kpi-rr" label="Receita Realizada (RR)" value={formatBRL(k.rr)} sub="Vendas efetivadas" tone="success" />
        <KpiCard testid="dashboard-kpi-rpr" label="Receita Potencial Remanescente (RPR)" value={formatBRL(k.rpr)} sub="Estoque no preço vigente" />
        <KpiCard testid="dashboard-kpi-ctl" label="Capital Investido nos Lotes (CTL)" value={formatBRL(k.capital_invested)} sub="Custo total dos lotes" />
        <KpiCard testid="dashboard-kpi-mgp" label="Margem Projetada Consolidada" value={formatPct(k.margin_pct)} sub={formatBRL(k.margin_rs)} tone={marginTone} />
        <KpiCard testid="dashboard-kpi-sellthrough" label="Sell-through Consolidado" value={formatPct(k.sell_through)} sub="RR ÷ RPA" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <RankingCard
          testid="ranking-margin-lots" title="Top Lotes por Margem (R$)" empty="Nenhum lote ainda"
          items={data.rankings.by_margin}
          render={(it) => (
            <>
              <p className="font-num text-sm font-semibold">{formatBRL(it.mgp_rs)}</p>
              <p className="text-xs text-muted-foreground">{formatPct(it.mgp_pct)}</p>
            </>
          )}
        />
        <RankingCard
          testid="ranking-age-lots" title="Lotes com Maior Idade (dias)" empty="Nenhum lote ainda"
          items={data.rankings.by_age}
          render={(it) => <p className="font-num text-sm font-semibold">{it.age_days ?? "—"} d</p>}
        />
        <RankingCard
          testid="ranking-markdown-lots" title="Maior Markdown Acumulado (R$)" empty="Nenhuma remarcação"
          items={data.rankings.by_markdown}
          render={(it) => (
            <>
              <p className="font-num text-sm font-semibold text-amber-600 dark:text-amber-400">{formatBRL(it.markdown_rs)}</p>
              <p className="text-xs text-muted-foreground">{formatPct(it.markdown_pct)}</p>
            </>
          )}
        />
        <RankingCard
          testid="ranking-recovery-lots" title="Menor Recuperação de Investimento" empty="Nenhuma venda ainda"
          items={data.rankings.by_recovery}
          render={(it) => <p className="font-num text-sm font-semibold">{formatPct(it.recovery_pct)}</p>}
        />
      </div>

      <div className="mt-8 rounded-xl border border-border bg-card shadow-sm" data-testid="dashboard-glossary">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Info className="h-4 w-4 text-accent" />
          <h3 className="font-display font-semibold">Legenda dos indicadores</h3>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 px-5 py-5 text-sm">
          {[
            ["RPI", "Receita Projetada Inicial — soma de quantidade recebida × preço inicial de cada mercadoria."],
            ["RR", "Receita Realizada — soma da receita líquida das vendas efetivadas (confirmadas) do lote."],
            ["RPR", "Receita Potencial Remanescente — estoque disponível × preço vigente (só peças ainda à venda)."],
            ["RPA", "Receita Projetada Atualizada = RR + RPR — o que já aconteceu mais o potencial atual do estoque."],
            ["CTL", "Custo Total do Lote — custos atribuíveis positivos menos descontos e abatimentos."],
            ["MGP", "Margem Gerencial Projetada = RPA − CTL (em R$) e MGP ÷ RPA × 100 (em %)."],
            ["Sell-through", "RR ÷ RPA × 100 — quanto do valor econômico projetado do lote já foi realizado."],
            ["Recuperação do Investimento", "RR ÷ CTL × 100 — quanto do capital investido já retornou em vendas."],
            ["Markdown Acumulado", "Redução de preço acumulada sobre o estoque remanescente (preço inicial vs. vigente)."],
            ["Capital Investido", "Soma dos CTLs dos lotes considerados."],
          ].map(([sigla, desc]) => (
            <div key={sigla} className="flex gap-2">
              <dt className="font-num font-semibold text-foreground whitespace-nowrap min-w-[52px]">{sigla}</dt>
              <dd className="text-muted-foreground">{desc}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="mt-6">
        <button onClick={() => navigate("/lotes")} className="inline-flex items-center gap-1 text-sm text-accent font-medium hover:underline" data-testid="dashboard-goto-lotes">
          Ver todos os lotes <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
